export type KanaScript = 'Hiragana' | 'Katakana'

export type KanaCell = {
    kana: string
    romaji: string
    audioUrl: string
    hasStrokeAsset?: boolean
}

export type KanaExtendedRow = {
    id: string
    label: string
    cells: KanaCell[]
}

export type KanaTable = {
    title: KanaScript
    baseRows: KanaCell[][]
    yoonRows: KanaCell[][]
    extendedRows: KanaExtendedRow[]
}

export type StrokeAsset = {
    kana: string
    imageUrl: string
    sourceUrl: string
}

export type PronunciationAudioAsset = {
    url: string
    sourceUrl: string
}

export type KanaSource = {
    label: string
    url: string
}

export type KanaDetailResponse = {
    kana: string
    romaji: string
    script: KanaScript
    wikipedia: {
        title: string
        extract: string
        url: string
    } | null
    pronunciationAudio: PronunciationAudioAsset | null
    strokeAssets: StrokeAsset[]
    tips: string[]
    sources: KanaSource[]
}

function getAudioFileName(romaji: string) {
    const normalizedRomaji = romaji.replace(/\s*\(.+?\)/g, '').trim().toLowerCase()
    return normalizedRomaji ? `${normalizedRomaji}.mp4` : ''
}

function kanaCell(kana: string, romaji: string, options?: { hasStrokeAsset?: boolean }): KanaCell {
    return {
        kana,
        romaji,
        audioUrl: getAudioFileName(romaji),
        hasStrokeAsset: options?.hasStrokeAsset ?? true
    }
}

export const EMPTY_CELL: KanaCell = kanaCell('', '')

export const vowels = ['a', 'i', 'u', 'e', 'o']
export const yoonVowels = ['ya', 'yu', 'yo']
export const extendedKanaGroupIds = [
    'fa-fi-fe-fo-fyu',
    'wi-we-wo-va-vi-ve-vo',
    'tsa-tsi-tse-tso',
    'che-she-je',
    'ti-di-du-tu'
] as const

export const hiraganaTable: KanaTable = {
    title: 'Hiragana',
    baseRows: [
        [kanaCell('あ', 'a'), kanaCell('い', 'i'), kanaCell('う', 'u'), kanaCell('え', 'e'), kanaCell('お', 'o')],
        [kanaCell('か', 'ka'), kanaCell('き', 'ki'), kanaCell('く', 'ku'), kanaCell('け', 'ke'), kanaCell('こ', 'ko')],
        [kanaCell('さ', 'sa'), kanaCell('し', 'shi'), kanaCell('す', 'su'), kanaCell('せ', 'se'), kanaCell('そ', 'so')],
        [kanaCell('た', 'ta'), kanaCell('ち', 'chi'), kanaCell('つ', 'tsu'), kanaCell('て', 'te'), kanaCell('と', 'to')],
        [kanaCell('な', 'na'), kanaCell('に', 'ni'), kanaCell('ぬ', 'nu'), kanaCell('ね', 'ne'), kanaCell('の', 'no')],
        [kanaCell('は', 'ha'), kanaCell('ひ', 'hi'), kanaCell('ふ', 'fu'), kanaCell('へ', 'he'), kanaCell('ほ', 'ho')],
        [kanaCell('ま', 'ma'), kanaCell('み', 'mi'), kanaCell('む', 'mu'), kanaCell('め', 'me'), kanaCell('も', 'mo')],
        [kanaCell('や', 'ya'), EMPTY_CELL, kanaCell('ゆ', 'yu'), EMPTY_CELL, kanaCell('よ', 'yo')],
        [kanaCell('ら', 'ra'), kanaCell('り', 'ri'), kanaCell('る', 'ru'), kanaCell('れ', 're'), kanaCell('ろ', 'ro')],
        [kanaCell('わ', 'wa'), EMPTY_CELL, EMPTY_CELL, EMPTY_CELL, kanaCell('を', 'wo')],
        [kanaCell('ん', 'n'), EMPTY_CELL, EMPTY_CELL, EMPTY_CELL, EMPTY_CELL],
        [kanaCell('が', 'ga'), kanaCell('ぎ', 'gi'), kanaCell('ぐ', 'gu'), kanaCell('げ', 'ge'), kanaCell('ご', 'go')],
        [kanaCell('ざ', 'za'), kanaCell('じ', 'ji'), kanaCell('ず', 'zu'), kanaCell('ぜ', 'ze'), kanaCell('ぞ', 'zo')],
        [kanaCell('だ', 'da'), kanaCell('ぢ', 'ji (di)'), kanaCell('づ', 'zu (du)'), kanaCell('で', 'de'), kanaCell('ど', 'do')],
        [kanaCell('ば', 'ba'), kanaCell('び', 'bi'), kanaCell('ぶ', 'bu'), kanaCell('べ', 'be'), kanaCell('ぼ', 'bo')],
        [kanaCell('ぱ', 'pa'), kanaCell('ぴ', 'pi'), kanaCell('ぷ', 'pu'), kanaCell('ぺ', 'pe'), kanaCell('ぽ', 'po')]
    ],
    yoonRows: [
        [kanaCell('きゃ', 'kya'), kanaCell('きゅ', 'kyu'), kanaCell('きょ', 'kyo')],
        [kanaCell('しゃ', 'sha'), kanaCell('しゅ', 'shu'), kanaCell('しょ', 'sho')],
        [kanaCell('ちゃ', 'cha'), kanaCell('ちゅ', 'chu'), kanaCell('ちょ', 'cho')],
        [kanaCell('にゃ', 'nya'), kanaCell('にゅ', 'nyu'), kanaCell('にょ', 'nyo')],
        [kanaCell('ひゃ', 'hya'), kanaCell('ひゅ', 'hyu'), kanaCell('ひょ', 'hyo')],
        [kanaCell('みゃ', 'mya'), kanaCell('みゅ', 'myu'), kanaCell('みょ', 'myo')],
        [kanaCell('りゃ', 'rya'), kanaCell('りゅ', 'ryu'), kanaCell('りょ', 'ryo')],
        [EMPTY_CELL, EMPTY_CELL, EMPTY_CELL],
        [kanaCell('ぎゃ', 'gya'), kanaCell('ぎゅ', 'gyu'), kanaCell('ぎょ', 'gyo')],
        [kanaCell('じゃ', 'ja'), kanaCell('じゅ', 'ju'), kanaCell('じょ', 'jo')],
        [EMPTY_CELL, EMPTY_CELL, EMPTY_CELL],
        [kanaCell('びゃ', 'bya'), kanaCell('びゅ', 'byu'), kanaCell('びょ', 'byo')],
        [kanaCell('ぴゃ', 'pya'), kanaCell('ぴゅ', 'pyu'), kanaCell('ぴょ', 'pyo')]
    ],
    extendedRows: []
}

export const katakanaTable: KanaTable = {
    title: 'Katakana',
    baseRows: [
        [kanaCell('ア', 'a'), kanaCell('イ', 'i'), kanaCell('ウ', 'u'), kanaCell('エ', 'e'), kanaCell('オ', 'o')],
        [kanaCell('カ', 'ka'), kanaCell('キ', 'ki'), kanaCell('ク', 'ku'), kanaCell('ケ', 'ke'), kanaCell('コ', 'ko')],
        [kanaCell('サ', 'sa'), kanaCell('シ', 'shi'), kanaCell('ス', 'su'), kanaCell('セ', 'se'), kanaCell('ソ', 'so')],
        [kanaCell('タ', 'ta'), kanaCell('チ', 'chi'), kanaCell('ツ', 'tsu'), kanaCell('テ', 'te'), kanaCell('ト', 'to')],
        [kanaCell('ナ', 'na'), kanaCell('ニ', 'ni'), kanaCell('ヌ', 'nu'), kanaCell('ネ', 'ne'), kanaCell('ノ', 'no')],
        [kanaCell('ハ', 'ha'), kanaCell('ヒ', 'hi'), kanaCell('フ', 'fu'), kanaCell('ヘ', 'he'), kanaCell('ホ', 'ho')],
        [kanaCell('マ', 'ma'), kanaCell('ミ', 'mi'), kanaCell('ム', 'mu'), kanaCell('メ', 'me'), kanaCell('モ', 'mo')],
        [kanaCell('ヤ', 'ya'), EMPTY_CELL, kanaCell('ユ', 'yu'), EMPTY_CELL, kanaCell('ヨ', 'yo')],
        [kanaCell('ラ', 'ra'), kanaCell('リ', 'ri'), kanaCell('ル', 'ru'), kanaCell('レ', 're'), kanaCell('ロ', 'ro')],
        [kanaCell('ワ', 'wa'), EMPTY_CELL, EMPTY_CELL, EMPTY_CELL, kanaCell('ヲ', 'wo')],
        [kanaCell('ン', 'n'), EMPTY_CELL, EMPTY_CELL, EMPTY_CELL, EMPTY_CELL],
        [kanaCell('ガ', 'ga'), kanaCell('ギ', 'gi'), kanaCell('グ', 'gu'), kanaCell('ゲ', 'ge'), kanaCell('ゴ', 'go')],
        [kanaCell('ザ', 'za'), kanaCell('ジ', 'ji'), kanaCell('ズ', 'zu'), kanaCell('ゼ', 'ze'), kanaCell('ゾ', 'zo')],
        [kanaCell('ダ', 'da'), kanaCell('ヂ', 'ji (di)'), kanaCell('ヅ', 'zu (du)'), kanaCell('デ', 'de'), kanaCell('ド', 'do')],
        [kanaCell('バ', 'ba'), kanaCell('ビ', 'bi'), kanaCell('ブ', 'bu'), kanaCell('ベ', 'be'), kanaCell('ボ', 'bo')],
        [kanaCell('パ', 'pa'), kanaCell('ピ', 'pi'), kanaCell('プ', 'pu'), kanaCell('ペ', 'pe'), kanaCell('ポ', 'po')]
    ],
    yoonRows: [
        [kanaCell('キャ', 'kya'), kanaCell('キュ', 'kyu'), kanaCell('キョ', 'kyo')],
        [kanaCell('シャ', 'sha'), kanaCell('シュ', 'shu'), kanaCell('ショ', 'sho')],
        [kanaCell('チャ', 'cha'), kanaCell('チュ', 'chu'), kanaCell('チョ', 'cho')],
        [kanaCell('ニャ', 'nya'), kanaCell('ニュ', 'nyu'), kanaCell('ニョ', 'nyo')],
        [kanaCell('ヒャ', 'hya'), kanaCell('ヒュ', 'hyu'), kanaCell('ヒョ', 'hyo')],
        [kanaCell('ミャ', 'mya'), kanaCell('ミュ', 'myu'), kanaCell('ミョ', 'myo')],
        [kanaCell('リャ', 'rya'), kanaCell('リュ', 'ryu'), kanaCell('リョ', 'ryo')],
        [EMPTY_CELL, EMPTY_CELL, EMPTY_CELL],
        [kanaCell('ギャ', 'gya'), kanaCell('ギュ', 'gyu'), kanaCell('ギョ', 'gyo')],
        [kanaCell('ジャ', 'ja'), kanaCell('ジュ', 'ju'), kanaCell('ジョ', 'jo')],
        [EMPTY_CELL, EMPTY_CELL, EMPTY_CELL],
        [kanaCell('ビャ', 'bya'), kanaCell('ビュ', 'byu'), kanaCell('ビョ', 'byo')],
        [kanaCell('ピャ', 'pya'), kanaCell('ピュ', 'pyu'), kanaCell('ピョ', 'pyo')]
    ],
    extendedRows: [
        {
            id: 'fa-fi-fe-fo-fyu',
            label: 'fa ・ fi ・ fe ・ fo ・ fyu',
            cells: [
                kanaCell('ファ', 'fa', { hasStrokeAsset: false }),
                kanaCell('フィ', 'fi', { hasStrokeAsset: false }),
                kanaCell('フェ', 'fe', { hasStrokeAsset: false }),
                kanaCell('フォ', 'fo', { hasStrokeAsset: false }),
                kanaCell('フュ', 'fyu', { hasStrokeAsset: false })
            ]
        },
        {
            id: 'wi-we-wo-va-vi-ve-vo',
            label: 'wi ・ we ・ wo ・ va ・ vi ・ ve ・ vo',
            cells: [
                kanaCell('ウィ', 'wi', { hasStrokeAsset: false }),
                kanaCell('ウェ', 'we', { hasStrokeAsset: false }),
                kanaCell('ウォ', 'wo', { hasStrokeAsset: false }),
                kanaCell('ヴァ', 'va', { hasStrokeAsset: false }),
                kanaCell('ヴィ', 'vi', { hasStrokeAsset: false }),
                kanaCell('ヴェ', 've', { hasStrokeAsset: false }),
                kanaCell('ヴォ', 'vo', { hasStrokeAsset: false })
            ]
        },
        {
            id: 'tsa-tsi-tse-tso',
            label: 'tsa ・ tsi ・ tse ・ tso',
            cells: [
                kanaCell('ツァ', 'tsa', { hasStrokeAsset: false }),
                kanaCell('ツィ', 'tsi', { hasStrokeAsset: false }),
                kanaCell('ツェ', 'tse', { hasStrokeAsset: false }),
                kanaCell('ツォ', 'tso', { hasStrokeAsset: false })
            ]
        },
        {
            id: 'che-she-je',
            label: 'che ・ she ・ je',
            cells: [
                kanaCell('チェ', 'che', { hasStrokeAsset: false }),
                kanaCell('シェ', 'she', { hasStrokeAsset: false }),
                kanaCell('ジェ', 'je', { hasStrokeAsset: false })
            ]
        },
        {
            id: 'ti-di-du-tu',
            label: 'ti ・ di ・ du ・ tu',
            cells: [
                kanaCell('ティ', 'ti', { hasStrokeAsset: false }),
                kanaCell('ディ', 'di', { hasStrokeAsset: false }),
                kanaCell('ドゥ', 'du', { hasStrokeAsset: false }),
                kanaCell('トゥ', 'tu', { hasStrokeAsset: false })
            ]
        }
    ]
}
